import * as React from "react";
import { cn } from "../lib/utils.ts";

export interface SeriesRowProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Horizontal snap-scroll row of posters. Netflix-style.
 * Scrolls naturally on mobile; on desktop adds chevron affordances.
 * `scroll-snap-type: x mandatory` so cards land cleanly on flick.
 */
export const SeriesRow = React.forwardRef<HTMLElement, SeriesRowProps>(
  ({ title, description, children, className, ...props }, ref) => {
    const scrollRef = React.useRef<HTMLDivElement>(null);

    const scrollBy = (delta: number) => {
      scrollRef.current?.scrollBy({ left: delta, behavior: "smooth" });
    };

    return (
      <section ref={ref} className={cn("relative space-y-3 py-4", className)} {...props}>
        <header className="flex items-end justify-between px-4 md:px-8">
          <div>
            <h2 className="text-xl font-semibold tracking-tight md:text-2xl">{title}</h2>
            {description && (
              <p className="mt-0.5 text-sm text-[var(--color-muted-foreground)]">{description}</p>
            )}
          </div>
          <div className="hidden gap-2 md:flex">
            <button
              type="button"
              aria-label="Scroll left"
              onClick={() => scrollBy(-800)}
              className="grid h-10 w-10 place-items-center rounded-full bg-[var(--color-secondary)] text-[var(--color-secondary-foreground)] transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
            >
              ‹
            </button>
            <button
              type="button"
              aria-label="Scroll right"
              onClick={() => scrollBy(800)}
              className="grid h-10 w-10 place-items-center rounded-full bg-[var(--color-secondary)] text-[var(--color-secondary-foreground)] transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
            >
              ›
            </button>
          </div>
        </header>

        <div
          ref={scrollRef}
          className="flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth px-4 pb-4 md:gap-4 md:px-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          tabIndex={0}
          role="region"
          aria-label={typeof title === "string" ? title : undefined}
        >
          {React.Children.map(children, (child) => (
            <div className="snap-start shrink-0 basis-[44%] sm:basis-[32%] md:basis-[22%] lg:basis-[16%] xl:basis-[14%]">
              {child}
            </div>
          ))}
        </div>
      </section>
    );
  },
);
SeriesRow.displayName = "SeriesRow";
